import { LitElement, css, nothing } from 'lit';
import { GeneratePushID } from '../../../modules/GeneratePushID.js';
import { Round } from '../../../modules/Round.js';
import { Convert } from '../../../modules/Convert.js';
import { GoogleGeometry } from '../../../modules/GoogleGeometry.js';
import firebase from 'firebase/compat/app';
import { KatapultDialog } from '../../katapult-elements/katapult-dialog.js';
import '../../katapult-elements/katapult-icon.js';

/**
 * @typedef {object} OverlayPositioningPoints
 * @property {number} [anchorPointXRelativeToImage] - The x coordinate of the anchor point relative to the top-left corner of the image
 * @property {number} [anchorPointYRelativeToImage] - The y coordinate of the anchor point relative to the top-left corner of the image
 * @property {number} [anchorPointXRelativeToView] - The x coordinate of the anchor point relative to the top-left corner of the map view
 * @property {number} [anchorPointYRelativeToView] - The y coordinate of the anchor point relative to the top-left corner of the map view
 * @property {number} [transformationPointXRelativeToView] - The x coordinate of the transformation point relative to the top-left corner of the map view
 * @property {number} [transformationPointYRelativeToView] - The y coordinate of the transformation point relative to the top-left corner of the map view
 */

const PIN_HEIGHT_OFFSET = 24;
const PIN_WIDTH_OFFSET = 12;

class ImageOverlay extends LitElement {
  static styles = css`
    /* This element renders no UI of its own — it only manages external DOM overlays */
    :host {
      display: none;
    }
  `;

  static properties = {
    map: { type: Object },
    jobId: { type: String },
    _overlayImageElement: { type: Object, state: true },
    _anchorPinElement: { type: Object, state: true },
    _anchorPinMapPosition: { type: Object, state: true },
    _transformationPinElement: { type: Object, state: true },
    _transformationPinMapPosition: { type: Object, state: true },
    _overlayPositioningPoints: { type: Object, state: true },
    _angleBetweenPoints: { type: Number, state: true },
    _distanceBetweenPoints: { type: Number, state: true },
    _imageScale: { type: Number, state: true },
    _imageRotation: { type: Number, state: true },
    _actionDialogData: { type: Object, state: true },
    _existingLayerData: { type: Object, state: true }
  };

  constructor() {
    super();
    /** @type {google.maps.Map|null} The google.maps.Map instance that the overlay is placed over */
    this.map = null;
    /** @type {string} */
    this.jobId = '';
    /** @type {HTMLImageElement|null} */
    this._overlayImageElement = null;
    /** @type {HTMLElement|null} */
    this._anchorPinElement = null;
    /** @type {google.maps.LatLng|null} The geographic LatLng of the anchor point */
    this._anchorPinMapPosition = null;
    /** @type {HTMLElement|null} */
    this._transformationPinElement = null;
    /** @type {google.maps.LatLng|null} The geographic LatLng of the transformation point */
    this._transformationPinMapPosition = null;
    /** @type {OverlayPositioningPoints}*/
    this._overlayPositioningPoints = {};
    /** @type {number|null} The angle between the anchor point and the transformation point */
    this._angleBetweenPoints = null;
    /** @type {number|null} The distance between the anchor point and the transformation point */
    this._distanceBetweenPoints = null;
    /** @type {number} The current scale of the image based on where the anchor and transformation points are */
    this._imageScale = 1;
    /** @type {number} The current rotation of the image based on where the anchor and transformation points are */
    this._imageRotation = 0;
    /**
     * @type {{sliderValue?: number, overlayName?: string, overlayDistance?: number | string, overlayBearing?: number | string}}
     * Dialog data shared with the action panel (e.g. sliderValue for opacity, overlayName, real-world distance in ft, and compass bearing in degrees).
     * Set by the parent; when sliderValue changes, overlay opacity is updated via updated().
     */
    this._actionDialogData = {};
    /** @type {object|null} The full layer data object when editing an existing overlay, or null when creating a new one */
    this._existingLayerData = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._onActionDialogDataChanged = (e) => {
      const newActionDialogData = e.detail.value ?? {};
      this._actionDialogData = { ...newActionDialogData };
    };
    document.addEventListener('action-dialog-data-changed', this._onActionDialogDataChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('action-dialog-data-changed', this._onActionDialogDataChanged);
  }

  render() {
    return nothing;
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('_actionDialogData')) {
      this.setImageOpacity();
      this.setImageTransformFromDistanceAndBearing();
    }
  }

  /**
   * Sets the overlay image's CSS opacity based on the action dialog's slider value
   */
  setImageOpacity() {
    const newOpacity = this._actionDialogData?.sliderValue;
    if (newOpacity == null || this._overlayImageElement == null) return;
    this._overlayImageElement.style.opacity = `${newOpacity / 100}`;
  }

  /**
   * Uses the action dialog's distance and bearing values to compute a new position for the transformation pin
   */
  setImageTransformFromDistanceAndBearing() {
    // Ensure all the necessary pieces of data are available before trying to compute the new position
    if (!this._overlayImageElement || !this._anchorPinElement) return;
    if (this._distanceBetweenPoints == null || this._angleBetweenPoints == null) return;
    if (!this._anchorPinMapPosition || !this._transformationPinMapPosition) return;

    // Get the new distance and bearing values from the action dialog and ensure they are valid numbers
    const newDistanceFt = Number(this._actionDialogData?.overlayDistance);
    const newBearing = Number(this._actionDialogData?.overlayBearing);
    if (isNaN(newDistanceFt) || isNaN(newBearing)) return;

    // If the distance and bearing haven't changed, don't do anything
    const { distance, bearing } = this.computeRealWorldDistanceAndBearing(this._anchorPinMapPosition, this._transformationPinMapPosition);
    if (newDistanceFt === Round(distance) && newBearing === Round(bearing)) return;

    // Compute the new transformation pin map position from the anchor, bearing, and distance, then convert to view pixels
    const newDistanceMeters = Convert(newDistanceFt, 'ft', 'm');
    const transformedResult = GoogleGeometry.maps.geometry.spherical.computeOffset(
      this._anchorPinMapPosition,
      newDistanceMeters,
      newBearing
    );
    const newTransformationLatLng = new google.maps.LatLng(transformedResult.lat(), transformedResult.lng());
    const proj = this._imageOverlayView.getProjection();
    const newTransformationPointOnView = proj.fromLatLngToDivPixel(newTransformationLatLng);
    this._overlayPositioningPoints.transformationPointXRelativeToView = newTransformationPointOnView.x;
    this._overlayPositioningPoints.transformationPointYRelativeToView = newTransformationPointOnView.y;

    // Update the image scale and rotation to the new values
    this.updateImageAndPinPositions();
  }

  /**
   * Opens the action dialog on the map with the given configuration
   * @param {object} actionDialogConfig - The configuration object for the action dialog
   */
  openActionDialog(actionDialogConfig) {
    const actionDialogEvent = new CustomEvent('open-action-dialog', {
      bubbles: true,
      composed: true,
      detail: actionDialogConfig
    });
    this.dispatchEvent(actionDialogEvent);
  }

  /**
   * Sets the action dialog data in katapult-maps-desktop
   * @param {object} actionDialogData - The data to set for the action dialog
   */
  setActionDialogData(actionDialogData) {
    const setDataEvent = new CustomEvent('set-action-dialog-data', {
      bubbles: true,
      composed: true,
      detail: actionDialogData
    });
    this.dispatchEvent(setDataEvent);
  }

  /**
   * Hides the drop-detector overlay that would otherwise intercept map clicks during anchor/chain placement.
   */
  changeDropDetectorVisibility(visible) {
    const changeVisibilityEvent = new CustomEvent('change-drop-detector-visibility', {
      bubbles: true,
      composed: true,
      detail: { visible }
    });
    this.dispatchEvent(changeVisibilityEvent);
  }

  /**
   * Toggles the given layer in katapult-maps-desktop
   * @param {object} layer - The layer to toggle
   * @param {boolean} checked - Whether the layer should be turned on (true) or off (false)
   */
  toggleMapLayer(layer, checked) {
    const toggleEvent = new CustomEvent('toggle-map-layer', {
      bubbles: true,
      composed: true,
      detail: { layer, checked }
    });
    this.dispatchEvent(toggleEvent);
  }

  /** Hide the image overlay details from the dialog so that when it closes, it goes away entirely */
  hideImageOverlayDetails() {
    const hideDetailsEvent = new CustomEvent('hide-image-overlay-details', {
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(hideDetailsEvent);
  }

  /**
   * Public function that starts the process of adding an image overlay on the map
   * @param {string} imageSource - The source URL for the overlay image
   */
  setUpImageOverlay(imageSource) {
    // Create an overlay view so the image can be inserted into the proper map pane
    this._imageOverlayView = new google.maps.OverlayView();

    // Set up the image when the overlay view is added to the map
    this._imageOverlayView.onAdd = () => this.setUpOverlayImageElement(imageSource);

    // Draw is called on every map pan or zoom, so this repositions the overlay to stay locked to the same geographic position when the map moves
    this._imageOverlayView.draw = () => this.keepOverlayLockedToMap();

    // Set up onRemove so it doesn't freak out when the overlay view is removed
    this._imageOverlayView.onRemove = () => {};

    // Add the overlay view to the map
    this._imageOverlayView.setMap(this.map);
  }

  /**
   * Public function that loads an existing image overlay layer for re-editing
   * @param {object} overlayLayerData - The layer data object including $key, name, and options.bounds
   */
  async editImageOverlay(overlayLayerData) {
    // Save the existing layer data
    this._existingLayerData = overlayLayerData;

    // Fetch the stored image URL from Firebase Storage
    const photoLocation = `job_files/${this.jobId}/${overlayLayerData.$key}`;
    const imageUrl = await firebase.storage().ref(photoLocation).getDownloadURL();

    // Set up the overlay with the fetched image URL
    this.setUpImageOverlay(imageUrl);
  }

  /**
   * Create the image and add it to the overlay view
   * @param {string} chosenOverlayImage - The source URL for the overlay image
   */
  setUpOverlayImageElement(chosenOverlayImage) {
    // Create the image element
    this._overlayImageElement = document.createElement('img');

    // Figure out the starting image dimensions and position
    const mapDiv = this.map.getDiv();
    const imageStartingHeight = mapDiv.offsetHeight * 0.5;
    const imageStartingTop = mapDiv.offsetHeight * 0.25;
    const imageStartingLeft = mapDiv.offsetWidth * 0.25;

    // Set the initial styles for the image overlay on the map
    const imageOverlayInitialStyles = {
      position: 'absolute',
      overflow: 'visible',
      height: `${imageStartingHeight}px`,
      top: `${imageStartingTop}px`,
      left: `${imageStartingLeft}px`,
      pointerEvents: 'none' // Turn off pointer events for the image so the clicks pass through to the map for positioning handlers
    };
    Object.assign(this._overlayImageElement.style, imageOverlayInitialStyles);

    // Set the cross origin attribute so the fetched image can be manipulated in a canvas when it's saved
    if (this._existingLayerData) this._overlayImageElement.crossOrigin = 'anonymous';

    // Set the image source
    this._overlayImageElement.src = chosenOverlayImage;

    // Insert the image into the overlayLayer pane so it renders below map annotations
    const panes = this._imageOverlayView.getPanes();
    panes.overlayLayer.appendChild(this._overlayImageElement);

    // When the image loads, start the process of positioning it on the map
    this._overlayImageElement.onload = () => this.startPlacingOverlayAction();
  }

  /**
   * Creates an action dialog for the overlay, then sets the first click handler to place an image
   */
  startPlacingOverlayAction() {
    // Open the action dialog with the appropriate configuration for image overlays
    this.openActionDialog({
      title: 'Edit Image Overlay',
      model: { icon: 'image:photo', color: 'var(--sl-color-neutral-700)' },
      icon: true,
      body: 'mapOverlayOptions',
      buttons: [
        {
          title: 'Save to Map',
          callback: () => this.saveImageOverlay(),
          attributes: { color: 'var(--secondary-color)' }
        }
      ],
      cancel: () => {
        const pageElement = document.getElementById('pageElement');
        this.cleanUpOverlayPositioning();
        pageElement?.cancelPromptAction();
      }
    });

    // Pre-fill the dialog's opacity slider and name from the existing layer when editing, otherwise default to 100% opacity and empty name
    const initialSliderValue = this._existingLayerData?.options?.opacity ?? 100;
    this.setActionDialogData({ sliderValue: initialSliderValue, overlayName: this._existingLayerData?.name ?? '' });

    // Disable Google Maps built-in clickable icons
    this.map.setOptions({ clickableIcons: false });

    // Temporarily hide the drop-detector overlay so it doesn't intercept clicks
    this.changeDropDetectorVisibility(false);

    // Position the image using the stored layer options when editing an existing overlay, otherwise place it at the center of the screen
    this.setInitialPositionForOverlay(this._existingLayerData?.options);

    // Listen for clicks on the map div and intercept clicks within the image bounds
    this.overlayImageClickedCallback = (mapClickEvent) => {
      // Determine if the click was on the image overlay
      const imageBoundingBox = this._overlayImageElement.getBoundingClientRect();
      const clickWasOnImageOverlay =
        mapClickEvent.clientX >= imageBoundingBox.left &&
        mapClickEvent.clientX <= imageBoundingBox.right &&
        mapClickEvent.clientY >= imageBoundingBox.top &&
        mapClickEvent.clientY <= imageBoundingBox.bottom;
      // If the click was not on the image, ignore it
      if (!clickWasOnImageOverlay) return;

      // If the ctrl key is being pressed, move the anchor point
      if (mapClickEvent.ctrlKey) {
        mapClickEvent.stopPropagation();
        this.moveAnchorPoint(mapClickEvent);
      }
      // If the shift key is being pressed place/move the transformation point to rotate and scale the image
      else if (mapClickEvent.shiftKey) {
        mapClickEvent.stopPropagation();
        this.placeTransformationPoint(mapClickEvent);
      }
    };
    this.map.getDiv().addEventListener('mousedown', this.overlayImageClickedCallback);
  }

  /**
   * Sets the anchor and transformation pins to their initial positions.
   * If `existingLayerOptions` is provided, the image is resized to cover the saved bounds and the saved anchor and rotation are restored.
   * Otherwise the image is placed at the center of the current map view.
   * @param {object} [existingLayerOptions] - Stored layer options when editing an existing overlay
   * @param {{north: number, south: number, east: number, west: number}} [existingLayerOptions.bounds] - Geographic bounds covered by the unrotated image
   * @param {number} [existingLayerOptions.rotation] - Saved image rotation in degrees
   * @param {number} [existingLayerOptions.relative_anchor_x] - Saved anchor x as a fraction of the image width
   * @param {number} [existingLayerOptions.relative_anchor_y] - Saved anchor y as a fraction of the image height
   */
  setInitialPositionForOverlay(existingLayerOptions) {
    const proj = this._imageOverlayView.getProjection();
    let anchorPinXRelativeToView, anchorPinYRelativeToView;
    const existingRotation = existingLayerOptions?.rotation ?? 0;

    // If existing layer options were provided, restore the image to its saved bounds, anchor, and rotation
    if (existingLayerOptions?.bounds) {
      const { bounds, relative_anchor_x: relativeAnchorX = 0.5, relative_anchor_y: relativeAnchorY = 0.5 } = existingLayerOptions;

      // Convert the bounds corners to overlay-view pixel coordinates
      const topLeftCornerOnView = proj.fromLatLngToDivPixel(new google.maps.LatLng(bounds.north, bounds.west));
      const bottomRightCornerOnView = proj.fromLatLngToDivPixel(new google.maps.LatLng(bounds.south, bounds.east));

      // Force the image element to match the saved bounds' pixel dimensions so the unrotated image lands exactly where it was saved
      const imagePixelWidth = bottomRightCornerOnView.x - topLeftCornerOnView.x;
      const imagePixelHeight = bottomRightCornerOnView.y - topLeftCornerOnView.y;
      this._overlayImageElement.style.width = `${imagePixelWidth}px`;
      this._overlayImageElement.style.height = `${imagePixelHeight}px`;

      // Restore the saved anchor point on the image so rotations and scales pivot around the same spot they did originally
      const anchorPointXRelativeToImage = relativeAnchorX * imagePixelWidth;
      const anchorPointYRelativeToImage = relativeAnchorY * imagePixelHeight;
      this._overlayPositioningPoints.anchorPointXRelativeToImage = anchorPointXRelativeToImage;
      this._overlayPositioningPoints.anchorPointYRelativeToImage = anchorPointYRelativeToImage;
      anchorPinXRelativeToView = topLeftCornerOnView.x + anchorPointXRelativeToImage;
      anchorPinYRelativeToView = topLeftCornerOnView.y + anchorPointYRelativeToImage;
    }
    // If no existing layer options were provided, place the image at the center of the map view with the anchor in the middle of the image
    else {
      // Set the anchor point to be the center of the image, so that rotations and scales pivot around the center by default
      this._overlayPositioningPoints.anchorPointXRelativeToImage = this._overlayImageElement.width / 2;
      this._overlayPositioningPoints.anchorPointYRelativeToImage = this._overlayImageElement.height / 2;

      // Convert the map center from geographic coordinates to pixel coordinates relative to the overlay view so the anchor pin can be placed there
      const mapCenterOnView = proj.fromLatLngToDivPixel(this.map.getCenter());
      anchorPinXRelativeToView = mapCenterOnView.x;
      anchorPinYRelativeToView = mapCenterOnView.y;
    }

    // Place the anchor pin
    this._overlayPositioningPoints.anchorPointXRelativeToView = anchorPinXRelativeToView;
    this._overlayPositioningPoints.anchorPointYRelativeToView = anchorPinYRelativeToView;
    this._anchorPinElement = this.placePinOnImage(anchorPinXRelativeToView, anchorPinYRelativeToView, 'anchor');

    // Initialize the transformation point coordinates to be offset from the anchor, then set the distance and angle so the image stays locked on map zoom and pan
    const effectiveImageWidth = parseFloat(this._overlayImageElement.style.width) || this._overlayImageElement.width;
    const transformationPointXRelativeToView = anchorPinXRelativeToView + effectiveImageWidth / 4;
    this._overlayPositioningPoints.transformationPointXRelativeToView = transformationPointXRelativeToView;
    this._overlayPositioningPoints.transformationPointYRelativeToView = anchorPinYRelativeToView;
    this._transformationPinElement = this.placePinOnImage(transformationPointXRelativeToView, anchorPinYRelativeToView, 'transformation');

    // Compute the initial distance and angle between the anchor and transformation points, then offset the angle by the saved rotation so the image renders at its saved orientation while keeping the transformation pin to the right of the anchor
    const { distanceBetweenPoints, angleBetweenPoints } = this.computeDistanceAndBearingBetweenPins();
    this._distanceBetweenPoints = distanceBetweenPoints;
    this._angleBetweenPoints = angleBetweenPoints - existingRotation;

    // Update the image and pin positions using the new points
    this.updateImageAndPinPositions();
  }

  /**
   * Converts the click position from page coordinates to coordinates relative to the overlay view them shifts then by the anchor point height and width
   * so the point is centered on the click location
   * @param {MouseEvent} mapClickEvent - The mouse event from clicking on the map
   * @returns {{x: number, y: number}} The coordinates of the click relative to the overlay view
   */
  clickPositionToPointOnView(mapClickEvent) {
    const proj = this._imageOverlayView.getProjection();
    const mapDivRect = this.map.getDiv().getBoundingClientRect();
    const containerX = mapClickEvent.clientX - mapDivRect.left;
    const containerY = mapClickEvent.clientY - mapDivRect.top;
    const viewPositionMapPoint = proj.fromContainerPixelToLatLng(new google.maps.Point(containerX, containerY));
    return proj.fromLatLngToDivPixel(viewPositionMapPoint);
  }

  /**
   * Moves the anchor point to the clicked location on the image
   * @param {MouseEvent} mapClickEvent - The mouse event from clicking on the map
   */
  moveAnchorPoint(mapClickEvent) {
    const { x: mouseXOnView, y: mouseYOnView } = this.clickPositionToPointOnView(mapClickEvent);

    // Undo the CSS transformations on the image to get the correct amount to shift the anchor point relative to the image
    const viewDeltaX = mouseXOnView - this._overlayPositioningPoints.anchorPointXRelativeToView;
    const viewDeltaY = mouseYOnView - this._overlayPositioningPoints.anchorPointYRelativeToView;
    const rotationRad = (this._imageRotation * Math.PI) / 180;
    const imageDeltaX = (viewDeltaX * Math.cos(rotationRad) + viewDeltaY * Math.sin(rotationRad)) / this._imageScale;
    const imageDeltaY = (-viewDeltaX * Math.sin(rotationRad) + viewDeltaY * Math.cos(rotationRad)) / this._imageScale;

    // Shift the anchor point relative to the image by the computed deltas
    this._overlayPositioningPoints.anchorPointXRelativeToImage += imageDeltaX;
    this._overlayPositioningPoints.anchorPointYRelativeToImage += imageDeltaY;
    this._overlayPositioningPoints.anchorPointXRelativeToView = mouseXOnView;
    this._overlayPositioningPoints.anchorPointYRelativeToView = mouseYOnView;

    // Update reference distance/angle so the current rotation and scale are preserved from the new anchor position
    const { distanceBetweenPoints, angleBetweenPoints } = this.computeDistanceAndBearingBetweenPins();
    this._distanceBetweenPoints = distanceBetweenPoints / this._imageScale;
    this._angleBetweenPoints = angleBetweenPoints - this._imageRotation;

    // Update the image and pin positions using the new points
    this.updateImageAndPinPositions();
  }

  /**
   * Updates the positioning points based on the current geographic position of the anchor and transformation pins,
   * then moves and scales the image to stay locked to those pins as the map moves underneath it.
   */
  keepOverlayLockedToMap() {
    const proj = this._imageOverlayView.getProjection();
    if (!this._anchorPinMapPosition || !this._transformationPinMapPosition || !proj) return;
    if (!this._anchorPinElement || !this._transformationPinElement) return;

    // Convert the anchor and transformation pins' geographic positions to pixel coordinates on the view
    const anchorPointOnView = proj.fromLatLngToDivPixel(this._anchorPinMapPosition);
    this._overlayPositioningPoints.anchorPointXRelativeToView = anchorPointOnView.x;
    this._overlayPositioningPoints.anchorPointYRelativeToView = anchorPointOnView.y;

    const transformationPointOnView = proj.fromLatLngToDivPixel(this._transformationPinMapPosition);
    this._overlayPositioningPoints.transformationPointXRelativeToView = transformationPointOnView.x;
    this._overlayPositioningPoints.transformationPointYRelativeToView = transformationPointOnView.y;

    // Update the image and pin positions using the new points
    this.updateImageAndPinPositions();
  }

  /**
   * Adds a pin to the map at the given page coordinates
   * @param {number} pinXRelativeToView - The x coordinate relative to the image where the pin should be placed
   * @param {number} pinYRelativeToView - The y coordinate relative to the image where the pin should be placed
   * @param {'anchor'|'transformation'} pinType
   * @param {object} options - Additional options for the pin
   * @returns {HTMLElement} - The pin element that was created and added to the map
   */
  placePinOnImage(pinXRelativeToView, pinYRelativeToView, pinType, options = {}) {
    // Create the pin element and position it so its tip is at the click location
    const anchor = document.createElement('katapult-icon');
    const anchorStyles = {
      cursor: 'pointer',
      pointerEvents: 'auto',
      height: `20px`,
      width: `20px`,
      left: `${pinXRelativeToView - PIN_WIDTH_OFFSET}px`,
      top: `${pinYRelativeToView - PIN_HEIGHT_OFFSET}px`,
      position: 'absolute'
    };
    Object.assign(anchor.style, anchorStyles);

    // Set the icon and color for the anchor
    anchor.icon = 'keep';
    anchor.color = pinType === 'anchor' ? 'red' : 'green';

    // Append the pin to overlayMouseTarget so it sits above the image and receives pointer events
    const panes = this._imageOverlayView.getPanes();
    panes.overlayMouseTarget.appendChild(anchor);

    // Set up the listeners to have the pin's movement update the relevant positioning points
    this.setUpPinMovementListeners(anchor, pinType, options);

    // Save the anchor element reference for later use
    return anchor;
  }

  /**
   * Set up the movement listeners for the given pin element so that when it's dragged, the relevant positioning points are updated and the image is transformed accordingly
   * @param {HTMLElement} pinElement - The pin element to set up the listeners for
   * @param {'anchor'|'transformation'} pinType
   * @param {object} options - Additional options for the pin movement
   */
  setUpPinMovementListeners(pinElement, pinType, options = {}) {
    const mapDiv = this.map.getDiv();
    const proj = this._imageOverlayView.getProjection();

    // When the mouse moves on the map, change the position of the anchor and update the overlay accordingly
    const mouseMovedOnMapCallback = (mapMouseMoveEvent) => {
      // Convert the mouse position on the map to the overlay view's coordinate space
      const mousePositionX = mapMouseMoveEvent.offsetX;
      const mousePositionY = mapMouseMoveEvent.offsetY;
      const mousePositionOnMap = proj.fromContainerPixelToLatLng(new google.maps.Point(mousePositionX, mousePositionY));
      const { x: mouseXOnView, y: mouseYOnView } = proj.fromLatLngToDivPixel(mousePositionOnMap);

      // Set the positioning points for the relevant pin based on the mouse position
      if (pinType === 'anchor') {
        // Get the previous anchor point coordinates so the transformation point can be moved by the same delta to stay fixed relative to the image
        const prevAnchorX = this._overlayPositioningPoints.anchorPointXRelativeToView;
        const prevAnchorY = this._overlayPositioningPoints.anchorPointYRelativeToView;

        // Move the transformation point by the same delta so it stays fixed relative to the image
        const dx = mouseXOnView - prevAnchorX;
        const dy = mouseYOnView - prevAnchorY;
        this._overlayPositioningPoints.transformationPointXRelativeToView += dx;
        this._overlayPositioningPoints.transformationPointYRelativeToView += dy;

        // Set the anchor point's positioning coordinates to the mouse position
        this._overlayPositioningPoints.anchorPointXRelativeToView = mouseXOnView;
        this._overlayPositioningPoints.anchorPointYRelativeToView = mouseYOnView;
      } else if (pinType == 'transformation') {
        // Set the transformation point's positioning coordinates to the mouse position
        this._overlayPositioningPoints.transformationPointXRelativeToView = mouseXOnView;
        this._overlayPositioningPoints.transformationPointYRelativeToView = mouseYOnView;
      }

      // Update the image and pin positions using the new points
      this.updateImageAndPinPositions();
    };

    // When the mouse is released, re-enable pointer events on the anchor and remove the mousemove listener since we're no longer dragging
    const mouseReleasedOnMapCallback = () => {
      // Re-enable map panning now that the drag is done
      this.map.setOptions({ draggable: true });
      // Re-enable pointer events on the anchor and image so it can be clicked again
      pinElement.style.pointerEvents = 'auto';
      // Remove the mousemove listener since we're no longer dragging
      mapDiv.removeEventListener('mousemove', mouseMovedOnMapCallback);
      // Remove this mouseup listener since it's a one-shot
      mapDiv.removeEventListener('mouseup', mouseReleasedOnMapCallback);
    };

    // Set up a callback to start dragging the pin
    const startDraggingPinCallback = (e) => {
      // Stop propagation so a click on any pin doesn't also bubble to the image-click handler
      e?.stopPropagation();
      // Disable map panning so the map doesn't pan while the pin is being dragged.
      this.map.setOptions({ draggable: false });
      // Turn off pointer events for the mouse so they pass through to the map while dragging
      pinElement.style.pointerEvents = 'none';
      // Add the listeners to the map
      mapDiv.addEventListener('mousemove', mouseMovedOnMapCallback);
      mapDiv.addEventListener('mouseup', mouseReleasedOnMapCallback);
    };

    // Disable the default dragging event and add the custom event listeners to the anchor
    pinElement.addEventListener('mousedown', startDraggingPinCallback);

    if (options.startDragging) startDraggingPinCallback();
  }

  /**
   * Use the overlay position points to move the image and pins on the accordingly
   */
  updateImageAndPinPositions() {
    const proj = this._imageOverlayView.getProjection();

    // Get all the relevant points for positioning the image and pins
    const {
      anchorPointXRelativeToView,
      anchorPointYRelativeToView,
      anchorPointXRelativeToImage,
      anchorPointYRelativeToImage,
      transformationPointXRelativeToView,
      transformationPointYRelativeToView
    } = this._overlayPositioningPoints;

    // Move the anchor pin so it sits at the anchor point and save its geographic position
    this._anchorPinElement.style.left = `${anchorPointXRelativeToView - PIN_WIDTH_OFFSET}px`;
    this._anchorPinElement.style.top = `${anchorPointYRelativeToView - PIN_HEIGHT_OFFSET}px`;
    const anchorPinPointOnView = new google.maps.Point(anchorPointXRelativeToView, anchorPointYRelativeToView);
    this._anchorPinMapPosition = proj.fromDivPixelToLatLng(anchorPinPointOnView);

    // Move the transformation pin so it sits at the transformation point and save its geographic position
    this._transformationPinElement.style.left = `${transformationPointXRelativeToView - PIN_WIDTH_OFFSET}px`;
    this._transformationPinElement.style.top = `${transformationPointYRelativeToView - PIN_HEIGHT_OFFSET}px`;
    const transformationMapPoint = new google.maps.Point(transformationPointXRelativeToView, transformationPointYRelativeToView);
    this._transformationPinMapPosition = proj.fromDivPixelToLatLng(transformationMapPoint);

    // Position the image so that the anchor point on the image corresponds with the anchor point's position in the view, then set the transform origin
    const imageX = anchorPointXRelativeToView - anchorPointXRelativeToImage;
    const imageY = anchorPointYRelativeToView - anchorPointYRelativeToImage;
    this._overlayImageElement.style.left = `${imageX}px`;
    this._overlayImageElement.style.top = `${imageY}px`;
    this._overlayImageElement.style.transformOrigin = `${anchorPointXRelativeToImage}px ${anchorPointYRelativeToImage}px`;

    // Compute the distance and bearing between the anchor point and the transformation point
    const { distanceBetweenPoints: newDistanceBetweenPoints, angleBetweenPoints: newAngleBetweenPoints } =
      this.computeDistanceAndBearingBetweenPins();

    // Set the image scale and rotation to be how much the distance and angle between the anchor and transformation point has changed relative to when the transformation point was first placed
    if (this._distanceBetweenPoints != null) this._imageScale = newDistanceBetweenPoints / this._distanceBetweenPoints;
    if (this._angleBetweenPoints != null) this._imageRotation = newAngleBetweenPoints - this._angleBetweenPoints;

    // Compute real-world distance and bearing, saved the rounded values, and update the action dialog
    const { distance, bearing } = this.computeRealWorldDistanceAndBearing(this._anchorPinMapPosition, this._transformationPinMapPosition);
    const roundedDistance = Round(distance);
    // If the distance is zero, preserve the bearing
    const roundedBearing = roundedDistance === 0 ? (this._actionDialogData?.overlayBearing ?? Round(bearing)) : Round(bearing);
    this.setActionDialogData({ overlayDistance: roundedDistance, overlayBearing: roundedBearing });

    // Apply the combined rotation and scale to the image with CSS transforms (rotation is applied before scale so they compound properly)
    this._overlayImageElement.style.transform = `rotate(${this._imageRotation}deg) scaleX(${this._imageScale}) scaleY(${this._imageScale})`;
  }

  /**
   * Computes the real-world distance in feet and compass bearing between two map points.
   * @param {google.maps.LatLng} mapPointFrom
   * @param {google.maps.LatLng} mapPointTo
   * @returns {{ distance: number, bearing: number }}
   */
  computeRealWorldDistanceAndBearing(mapPointFrom, mapPointTo) {
    // Normalize to GoogleGeometry.maps.LatLng instances
    const from = new GoogleGeometry.maps.LatLng(mapPointFrom.lat(), mapPointFrom.lng());
    const to = new GoogleGeometry.maps.LatLng(mapPointTo.lat(), mapPointTo.lng());
    const spherical = GoogleGeometry.maps.geometry.spherical;
    const distance = Convert(spherical.computeDistanceBetween(from, to), 'm', 'ft');
    const bearing = (spherical.computeHeading(from, to) + 360) % 360;
    return { distance, bearing };
  }

  /**
   * Computes the distance and bearing between the anchor point and the transformation point.
   * @returns {{ distanceBetweenPoints: number, angleBetweenPoints: number }} The distance in pixels and the bearing in degrees between the two points
   */
  computeDistanceAndBearingBetweenPins() {
    // Get the relevant points to compute the distance and bearing between the anchor and transformation point
    const {
      anchorPointXRelativeToView,
      anchorPointYRelativeToView,
      transformationPointXRelativeToView,
      transformationPointYRelativeToView
    } = this._overlayPositioningPoints;

    // Get the x and y deltas between the control point and the transformation point.
    const anchorTransformDeltaX = transformationPointXRelativeToView - anchorPointXRelativeToView;
    // Invert the y delta since the page coordinate system has y increasing downwards but we want it to increase upwards for the angle calculation
    const anchorTransformDeltaY = (transformationPointYRelativeToView - anchorPointYRelativeToView) * -1;

    // Calculate the angle of the vector from the anchor point to the transformation point relative to the positive x-axis in degrees.
    const RAD_TO_DEG = 180 / Math.PI;
    // Atan2 yields angles between -π and π, so we convert to degrees,
    // add 360 to always have a positive angle,
    // then modulo by 360 to get an angle between 0 and 360.
    const anchorTransformAngle = (360 + Math.atan2(anchorTransformDeltaY, anchorTransformDeltaX) * RAD_TO_DEG) % 360;
    // To convert from ccw from positive x-axis to cw from north,
    // subtract the angle from 360 to flip to clockwise,
    // then add 90 to shift from being relative to the positive x-axis to being relative to north,
    // then modulo by 360 to get an angle between 0 and 360.
    const anchorTransformAzimuth = (360 + 90 - anchorTransformAngle) % 360;

    // Compute the distance between the anchor point and the transformation point
    const anchorTransformationDistance = Math.sqrt(anchorTransformDeltaX ** 2 + anchorTransformDeltaY ** 2);

    return { distanceBetweenPoints: anchorTransformationDistance, angleBetweenPoints: anchorTransformAzimuth };
  }

  /**
   * Places or replaces the transformation pin at the clicked location on the image and sets up data for image transformations
   * @param {MouseEvent} e - The mousedown event from clicking on the overlay image
   */
  placeTransformationPoint(e) {
    const { x: mouseXOnView, y: mouseYOnView } = this.clickPositionToPointOnView(e);

    // Set the transformation point's positioning coordinates to the mouse position
    this._overlayPositioningPoints.transformationPointXRelativeToView = mouseXOnView;
    this._overlayPositioningPoints.transformationPointYRelativeToView = mouseYOnView;

    // Compute measurements between points and set them for to use for future transformations
    const { distanceBetweenPoints, angleBetweenPoints } = this.computeDistanceAndBearingBetweenPins();

    // Scale the distance and bearing by the current rotation and scale so placing the pin does not change the image transform
    this._distanceBetweenPoints = distanceBetweenPoints / this._imageScale;
    this._angleBetweenPoints = angleBetweenPoints - this._imageRotation;

    // Update pin positions and save the new geographic position without changing the image transform
    this.updateImageAndPinPositions();

    // Remove the old pin and place a new one at the clicked location then make the point start dragging immediately
    // Note: this needs to be done after updating the positions so that the map listeners don't affect where the transformation pin is initially placed
    this._transformationPinElement.remove();
    const transformationPinElement = this.placePinOnImage(mouseXOnView, mouseYOnView, 'transformation', { startDragging: true });
    this._transformationPinElement = transformationPinElement;
  }

  /**
   * Derives the geographic bounds of the positioned overlay, saves the layer metadata to the database, and uploads the original image blob to Firebase Storage.
   * @returns {Promise<string>} The push ID key of the newly created layer
   */
  async generateAndSaveLayer() {
    // Get the geographic bounds of the scaled image without rotation
    const imageMapBounds = this.getImageBoundsWithoutRotation();

    // Create the bounds for a layer based on the image's map bounds
    const layerBounds = {
      north: imageMapBounds.getNorthEast().lat(),
      south: imageMapBounds.getSouthWest().lat(),
      east: imageMapBounds.getNorthEast().lng(),
      west: imageMapBounds.getSouthWest().lng()
    };

    // Save the anchor as a fraction of the image dimensions so it scales with the overlay div as the user zooms in and out
    const imageWidth = this._overlayImageElement.clientWidth;
    const imageHeight = this._overlayImageElement.clientHeight;
    const relativeAnchorX = this._overlayPositioningPoints.anchorPointXRelativeToImage / imageWidth;
    const relativeAnchorY = this._overlayPositioningPoints.anchorPointYRelativeToImage / imageHeight;

    // Save the layer to the database
    const layerKey = this._existingLayerData?.$key ?? GeneratePushID();
    const overlayName = this._actionDialogData?.overlayName ?? '';
    const layerData = {
      name: overlayName || 'Image Overlay',
      type: 'Overlay Layer',
      options: {
        bounds: layerBounds,
        rotation: this._imageRotation,
        opacity: this._actionDialogData?.sliderValue ?? 100,
        relative_anchor_x: relativeAnchorX,
        relative_anchor_y: relativeAnchorY,
        // Legacy image overlays created outside of this system won't be able to be edited
        editable: true
      }
    };
    await globalThis.FirebaseWorker.ref(`photoheight/jobs/${this.jobId}/layers/list/${layerKey}`).update(layerData);

    // Only upload the image to Firebase storage if it's a new layer
    if (!this._existingLayerData) {
      const originalBlob = await fetch(this._overlayImageElement.src).then((r) => r.blob());
      await firebase.storage().ref(`job_files/${this.jobId}/${layerKey}`).put(originalBlob);
    }

    return layerKey;
  }

  /**
   * Saves the positioned overlay as a map layer in the database and Firebase Storage, toggles it on, then cleans up the placement workflow.
   */
  async saveImageOverlay() {
    try {
      // Turn off pointer events for the pins so you can't move the image while it's being saved
      this._anchorPinElement.style.pointerEvents = 'none';
      this._transformationPinElement.style.pointerEvents = 'none';

      // Swap the dialog body to a saving spinner while the overlay is being saved
      this.openActionDialog({
        title: 'Edit Image Overlay',
        model: { icon: 'image:photo', color: 'var(--sl-color-neutral-700)' },
        icon: true,
        body: 'savingOverlay',
        buttons: []
      });

      // Save the image to the map
      const layerKey = await this.generateAndSaveLayer();

      // Get the layer that was just saved to the data to toggle it
      const layerPath = `photoheight/jobs/${this.jobId}/layers/list/${layerKey}`;
      const layerToToggle = (await globalThis.FirebaseWorker.ref(layerPath).once('value')).val();
      layerToToggle.$key = layerKey;

      // Toggle the layer
      this.toggleMapLayer(layerToToggle, true);
    } catch (error) {
      // Show a toast at the bottom of the map letting the user know the save failed
      this.dispatchEvent(
        new CustomEvent('toast', {
          bubbles: true,
          composed: true,
          detail: { message: 'Image overlay could not be saved.' }
        })
      );
    } finally {
      // Clean up the overlay positioning so the map returns to its normal state
      this.cleanUpOverlayPositioning();

      // Close the action dialog
      const pageElement = document.getElementById('pageElement');
      pageElement?.cancelPromptAction();
    }
  }

  /**
   * Cancels the overlay workflow and removes all relevant listeners, pins, and the image from the map
   */
  cleanUpOverlayPositioning() {
    this.hideImageOverlayDetails();

    // Remove the image click listener and draw hook so the map can go back to its normal behavior
    this.map.getDiv().removeEventListener('mousedown', this.overlayImageClickedCallback);
    this._imageOverlayView.draw = () => {};

    // Remove the pins and image from the map
    this._overlayImageElement?.remove();
    this._anchorPinElement?.remove();
    this._transformationPinElement.remove();
    this._overlayImageElement = null;
    this._anchorPinElement = null;
    this._transformationPinElement = null;
    this._anchorPinMapPosition = null;
    this._transformationPinMapPosition = null;

    // Reset the map by removing the overlay and making the built-in clickable icons interactive again
    this._imageOverlayView?.setMap(null);
    this._imageOverlayView = null;
    this.map.setOptions({ clickableIcons: true });

    // Restore the drop detector to its normal expanded state
    this.changeDropDetectorVisibility(true);

    this._existingLayerData = null;
  }

  /**
   * Calculates the geographic bounding box of the overlay image in its current position, ignoring rotation.
   * @returns {google.maps.LatLngBounds} The geographic bounds enclosing the image without rotation
   */
  getImageBoundsWithoutRotation() {
    const proj = this._imageOverlayView.getProjection();
    const { anchorPointXRelativeToView, anchorPointYRelativeToView } = this._overlayPositioningPoints;
    const anchorXRelativeToImage = this._overlayPositioningPoints.anchorPointXRelativeToImage;
    const anchorYRelativeToImage = this._overlayPositioningPoints.anchorPointYRelativeToImage;

    // Compute data needed to calculate the bounds of the image
    const imageWidth = this._overlayImageElement.clientWidth;
    const imageHeight = this._overlayImageElement.clientHeight;
    const scale = this._imageScale;

    // Compute the top-left corner of the scaled image without rotation
    const topLeftX = anchorPointXRelativeToView - anchorXRelativeToImage * scale;
    const topLeftY = anchorPointYRelativeToView - anchorYRelativeToImage * scale;
    const topLeft = proj.fromDivPixelToLatLng(new google.maps.Point(topLeftX, topLeftY));

    // Compute the bottom-right corner of the scaled image without rotation
    const bottomRightX = anchorPointXRelativeToView - anchorXRelativeToImage * scale + imageWidth * scale;
    const bottomRightY = anchorPointYRelativeToView - anchorYRelativeToImage * scale + imageHeight * scale;
    const bottomRight = proj.fromDivPixelToLatLng(new google.maps.Point(bottomRightX, bottomRightY));

    // Create the bounds with the corners and return it
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(topLeft);
    bounds.extend(bottomRight);
    return bounds;
  }
}

customElements.define('image-overlay', ImageOverlay);
